import { DnaHelixStrip } from './DnaHelixStrip';
import type { ScrollyPlatformItem, StickyDnaScrollSectionProps } from './StickyDnaScrollSection';

const DNA_STATIC_REPEAT = 4;

export function ScrollyStaticMobileSection({
  steps,
  sourcesSectionTitle,
  sourceCards,
  hintEyebrow,
  hintMainText,
  hintLeadIcon,
  hintDocNote,
  platformsEyebrow,
  powerTitle,
  powerSubtitle,
  fitPrefix,
  fitHighlight,
  fitSuffix,
  platforms,
  extraBubbles,
  toolsLink,
  overview,
  infoSectionTitle,
  infoBlocks,
}: StickyDnaScrollSectionProps) {
  return (
    <div className="scrolly-static">
      <div className="scrolly-static__dna" aria-hidden>
        <div className="scrolly-static__dna-inner">
          <DnaHelixStrip
            repeat={DNA_STATIC_REPEAT}
            fade="none"
            className="scrolly-dna-svg scrolly-static__dna-svg"
          />
        </div>
      </div>

      <section className="scrolly-static__section">
        <div className="scrolly-s1-copy scrolly-static__steps">
          {steps.slice(0, 2).map(step => (
            <article key={step.id} className="scrolly-s1-step">
              <h2 className="scrolly-s1-step__title">{step.title}</h2>
              {step.body?.trim() ? <p className="scrolly-s1-step__body">{step.body}</p> : null}
            </article>
          ))}
        </div>
      </section>

      <section className="scrolly-static__section">
        <div className="scrolly-s2-inner">
          <h2 className="scrolly-s2-title">{sourcesSectionTitle}</h2>
          <div className="scrolly-s2-cards scrolly-static__source-cards" role="list">
            {sourceCards.slice(0, 4).map((card, i) => (
              <article key={card.id} className="scrolly-s2-card" role="listitem">
                <span className="scrolly-s2-card__index" aria-hidden>
                  {String(i + 1).padStart(2, '0')}
                </span>
                <h3 className="scrolly-s2-card__title">{card.title}</h3>
                {card.body?.trim() ? <p className="scrolly-s2-card__body">{card.body}</p> : null}
              </article>
            ))}
          </div>
        </div>
      </section>

      <section className="scrolly-static__section scrolly-static__section--hint">
        <div className="scrolly-s3-inner">
          <p className="about-page__eyebrow scrolly-s3-eyebrow">{hintEyebrow}</p>
          <div className="scrolly-s3-lead scrolly-static__hint-lead">
            <span className="scrolly-s3-lead__icon" aria-hidden>
              {hintLeadIcon}
            </span>
            <blockquote className="scrolly-s3-quote">{hintMainText}</blockquote>
          </div>
          <p className="scrolly-s3-doc-note">{hintDocNote}</p>
        </div>
      </section>

      <section className="scrolly-static__section scrolly-static__section--platforms">
        {overview ? <div className="scrolly-static__foundations">{overview}</div> : null}
        <div className="scrolly-s4-inner scrolly-static__platforms-inner">
          <div className="scrolly-s4-left scrolly-static__platforms-copy">
            <p className="about-page__eyebrow">{platformsEyebrow}</p>
            <h2 className="scrolly-s4-title">{powerTitle}</h2>
            <p className="scrolly-s4-subtitle">{powerSubtitle}</p>
            <p className="scrolly-s4-fit">
              {fitPrefix}
              <strong className="scrolly-s4-fit__highlight">{fitHighlight}</strong>
              {fitSuffix}
            </p>
            <small>{toolsLink}</small>
          </div>

          <div className="scrolly-static-bubbles" role="list">
            {platforms.slice(0, 3).map(p => (
              <StaticBubble key={p.id} item={p} />
            ))}
            {(extraBubbles ?? []).slice(0, 2).map(p => (
              <StaticBubble key={p.id} item={p} />
            ))}
          </div>
        </div>
      </section>

      {!overview ? (
        <section
          className="scrolly-s5-wrap scrolly-static__section"
          aria-labelledby="about-page-info-heading"
        >
          <div className="scrolly-s5-inner">
            <h2 id="about-page-info-heading" className="about-page__info-heading">
              {infoSectionTitle}
            </h2>
            <ul className="about-page__info-grid">
              {(infoBlocks ?? []).map(block => (
                <li key={block.id}>
                  <div className="about-page__info-block">
                    <h3 className="about-page__info-block-title">{block.title}</h3>
                    <p className="about-page__info-block-body">{block.body}</p>
                  </div>
                </li>
              ))}
            </ul>
          </div>
        </section>
      ) : null}
    </div>
  );
}

function StaticBubble({ item }: { item: ScrollyPlatformItem }) {
  return (
    <div className="scrolly-s4-bubble scrolly-static-bubble" role="listitem">
      <div className="scrolly-s4-bubble__ring">
        <span className="scrolly-s4-bubble__icon">{item.icon}</span>
        <span className="scrolly-s4-bubble__title">{item.title}</span>
        <span className="scrolly-s4-bubble__desc">{item.description}</span>
      </div>
      <span className="scrolly-s4-bubble__hint">{item.hint}</span>
    </div>
  );
}
